import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { FadeUp, SectionTag } from "../components/motion";
import { CONTACT, REQUIREMENT_TYPES } from "../data/content";
import useSEO from "../hooks/useSEO";

const inputCls = "w-full px-4 py-3 rounded-lg bg-[#F1F5F9] border border-slate-300 text-sm text-slate-700 placeholder:text-slate-600 focus:outline-none focus:border-blue-600/60 transition-colors";

const INFO = [
  { icon: MapPin, label: "Head Office", value: CONTACT.address },
  { icon: Phone, label: "Phone", value: CONTACT.phones.join("  ·  ") },
  { icon: Mail, label: "Email", value: CONTACT.email },
  { icon: Clock, label: "Response Time", value: "Every enquiry answered within 1–2 business days" },
];

export default function Contact() {
  useSEO(
    "Contact SATI Global Systems Patna — CCTV, Networking & IT Project Enquiry | SGSNet",
    "Contact SATI Global Systems (SGSNet), Bailey Road, Patna — call +91 77599 20111 or email mysgsinfo@gmail.com for CCTV camera, surveillance, networking, software, EV and IT project enquiries."
  );
  const [form, setForm] = useState({
    name: "", company: "", designation: "", email: "", phone: "",
    country: "", requirement_type: "", description: "", timeline: "", consent: false,
  });
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the consent to proceed.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      if (attachment) fd.append("attachment", attachment);
      await api.post("/enquiries", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Enquiry sent. Our team will respond within 1–2 business days.");
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-16 sm:pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp><SectionTag>Contact Us</SectionTag></FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.08] max-w-3xl">
            Let's discuss <span className="text-blue-600">your project.</span>
          </h1>
        </FadeUp>

        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-4">
            {INFO.map((item, i) => (
              <FadeUp key={item.label} delay={i * 0.08}>
                <div className="bg-panel border border-slate-200 rounded-xl p-6" data-testid={`contact-info-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <item.icon size={20} className="text-blue-600" strokeWidth={1.7} />
                  <h3 className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{item.label}</h3>
                  <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{item.value}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <div className="lg:col-span-8">
            <FadeUp delay={0.15}>
              {sent ? (
                <div className="h-full min-h-[380px] flex flex-col items-center justify-center text-center bg-panel border border-emerald-300 rounded-2xl p-10" data-testid="contact-success">
                  <span className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-300 flex items-center justify-center text-emerald-600 text-2xl font-heading">✓</span>
                  <h2 className="mt-6 font-heading text-2xl font-bold text-slate-900">Enquiry received</h2>
                  <p className="mt-3 max-w-md text-sm text-slate-600 leading-relaxed">
                    Thank you, {form.name.split(" ")[0] || "there"}. A confirmation has been sent to {form.email}.
                    Our solutions team will respond within 1–2 business days.
                  </p>
                  <button onClick={() => { setSent(false); setForm({ name: "", company: "", designation: "", email: "", phone: "", country: "", requirement_type: "", description: "", timeline: "", consent: false }); setAttachment(null); }}
                    data-testid="contact-send-another"
                    className="mt-8 px-6 py-3 rounded-full border border-slate-300 text-sm text-slate-700 hover:border-blue-600/60 hover:text-blue-600 transition-colors">
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-panel border border-slate-200 rounded-2xl p-7 sm:p-9 space-y-4" data-testid="contact-form">
                  <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Send an Enquiry</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
                    <input placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} data-testid="contact-company-input" />
                    <input placeholder="Designation" value={form.designation} onChange={set("designation")} className={inputCls} data-testid="contact-designation-input" />
                    <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
                    <input placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
                    <input placeholder="Country" value={form.country} onChange={set("country")} className={inputCls} data-testid="contact-country-input" />
                    <select required value={form.requirement_type} onChange={set("requirement_type")} className={inputCls} data-testid="contact-requirement-select">
                      <option value="">Requirement type *</option>
                      {REQUIREMENT_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <input placeholder="Expected timeline" value={form.timeline} onChange={set("timeline")} className={inputCls} data-testid="contact-timeline-input" />
                  </div>
                  <textarea required rows={5} placeholder="Project description *" value={form.description} onChange={set("description")} className={inputCls} data-testid="contact-description-input" />
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <label className="flex items-center gap-2.5 cursor-pointer" data-testid="contact-attachment-upload">
                      <Paperclip size={15} className="text-blue-600" />
                      <span className="text-xs text-slate-500">{attachment ? attachment.name : "Attach RFP / document (optional, max 10 MB)"}</span>
                      <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.zip" className="hidden" onChange={(e) => setAttachment(e.target.files[0] || null)} />
                    </label>
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input type="checkbox" checked={form.consent} onChange={set("consent")}
                        className="w-4 h-4 rounded border-slate-600 bg-[#F1F5F9] accent-blue-600" data-testid="contact-consent-checkbox" />
                      <span className="text-xs text-slate-500">I consent to SGS contacting me about this enquiry *</span>
                    </label>
                  </div>
                  <button type="submit" disabled={submitting} data-testid="contact-submit-button"
                    className="w-full sm:w-auto px-8 py-4 rounded-full bg-blue-600 text-[#FFFFFF] font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50">
                    {submitting ? "Sending…" : "Send Enquiry"}
                  </button>
                </form>
              )}
            </FadeUp>
          </div>
        </div>
      </div>
    </main>
  );
}
